import { useState } from "react";

const Section = ({ children, style }) => (
  <div style={{ marginBottom: "2rem", ...style }}>{children}</div>
);

const SectionTitle = ({ color, children }) => (
  <div style={{
    background: color,
    color: "#fff",
    fontWeight: 700,
    fontSize: "13px",
    letterSpacing: "0.08em",
    padding: "6px 18px",
    borderRadius: "6px",
    display: "inline-block",
    marginBottom: "16px",
    textTransform: "uppercase"
  }}>{children}</div>
);

const Badge = ({ color, bg, children }) => (
  <span style={{
    background: bg,
    color,
    fontSize: "11px",
    fontWeight: 600,
    padding: "2px 10px",
    borderRadius: "20px",
    letterSpacing: "0.05em"
  }}>{children}</span>
);

const CheckItem = ({ color = "#1D9E75", children }) => (
  <li style={{ display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "6px", listStyle: "none", fontSize: "13px", color: "#444" }}>
    <span style={{ color, fontWeight: 700, marginTop: "1px", flexShrink: 0 }}>✓</span>
    <span>{children}</span>
  </li>
);

const layers = [
  {
    id: "l4",
    label: "Layer 4 Load Balancer",
    sub: "Transport Layer",
    color: "#7F77DD",
    bg: "#EEEDFE",
    icon: "⇄",
    routes: "Routes based on IP Address and TCP/UDP Ports",
    bullets: [
      "Works at Transport Layer",
      "Does not inspect request content",
      "Extremely fast & efficient",
      "Ideal when speed & performance are the highest priority"
    ],
    examples: "AWS Network Load Balancer, HAProxy (L4 mode)"
  },
  {
    id: "l7",
    label: "Layer 7 Load Balancer",
    sub: "Application Layer",
    color: "#185FA5",
    bg: "#E6F1FB",
    icon: "⊞",
    routes: "Routes based on Content (HTTP Headers, Cookies, URL, Query String, User Location, etc.)",
    bullets: [
      "Works at Application Layer",
      "Inspects actual request content",
      "Enables intelligent routing decisions",
      "More flexible but requires more processing power"
    ],
    examples: "AWS Application Load Balancer, Nginx"
  }
];

const deployments = [
  {
    label: "Hardware Load Balancers",
    color: "#185FA5",
    bg: "#E6F1FB",
    icon: "🖥",
    desc: "Physical devices built for high performance.",
    checks: [
      "Used in large enterprises / data centers",
      "Handles millions of requests per second",
      "Built-in features: SSL termination, DDoS protection, advanced traffic management"
    ],
    examples: "F5 Networks BIG-IP, Citrix NetScaler"
  },
  {
    label: "Software Load Balancers",
    color: "#0F6E56",
    bg: "#E1F5EE",
    icon: "⚙️",
    desc: "Applications running on general-purpose servers.",
    checks: [
      "Flexible and cost-effective",
      "No dedicated hardware needed",
      "Can run on VM, containers, or on-prem infrastructure",
      "Widely used in microservices and Kubernetes"
    ],
    examples: "Nginx, HAProxy, Envoy"
  },
  {
    label: "Cloud-Based Load Balancers",
    color: "#853708",
    bg: "#FAEEDA",
    icon: "☁️",
    desc: "Managed services by cloud providers.",
    checks: [
      "Automatically distributes traffic across resources & regions",
      "No infra management",
      "High scalability, security & global availability"
    ],
    examples: "AWS Elastic Load Balancer, Google Cloud Load Balancer, Azure Load Balancer"
  }
];

const staticStrategies = [
  {
    id: "rr",
    label: "Round Robin",
    num: "1)",
    color: "#534AB7",
    bg: "#EEEDFE",
    desc: "Sends requests sequentially to each server in a circular order.",
    detail: "1→S1  2→S2  3→S3  4→S1 …",
    tag: "Ensures even distribution"
  },
  {
    id: "wrr",
    label: "Weighted Round Robin",
    num: "2)",
    color: "#185FA5",
    bg: "#E6F1FB",
    desc: "Servers receive requests based on their assigned weight.",
    detail: "S1(W5)  S2(W3)  S3(W1)",
    tag: "Ideal when server capacities differ"
  },
  {
    id: "iphash",
    label: "IP Hash",
    num: "3)",
    color: "#0F6E56",
    bg: "#E1F5EE",
    desc: "Requests from the same IP always go to the same server.",
    detail: "IP1→S1  IP2→S2  IP3→S3",
    tag: "Ensures session persistence"
  }
];

const dynamicStrategies = [
  {
    id: "lc",
    label: "Least Connections",
    num: "1)",
    color: "#993C1D",
    bg: "#FAECE7",
    desc: "Sends requests to the server with the fewest active connections.",
    detail: "S1(10)  S2(8)  S3(7) → picks S3",
    tag: "Keeps load balanced on current connections"
  },
  {
    id: "lr",
    label: "Least Response Time",
    num: "2)",
    color: "#853F0A",
    bg: "#FAEEDA",
    desc: "Sends requests to the server with the lowest response time.",
    detail: "S1(120ms)  S2(40ms)  S3(80ms) → picks S2",
    tag: "Improves performance by choosing the fastest server"
  },
  {
    id: "rb",
    label: "Resource Based",
    num: "3)",
    color: "#3B6D11",
    bg: "#EAF3DE",
    desc: "Considers server resources like CPU, memory, etc.",
    detail: "S1 CPU80%  S2 CPU30%  S3 CPU60% → picks S2",
    tag: "Distributes load based on available resources"
  }
];

const StrategyCard = ({ s, active, onClick }) => (
  <div
    onClick={() => onClick(s.id)}
    style={{
      background: active ? s.bg : "#fafafa",
      border: `2px solid ${active ? s.color : "#e2e2e2"}`,
      borderRadius: "12px",
      padding: "16px",
      cursor: "pointer",
      transition: "all 0.18s",
      flex: "1 1 180px",
      minWidth: "160px"
    }}
  >
    <div style={{ fontSize: "12px", fontWeight: 700, color: s.color, marginBottom: "4px" }}>{s.num} {s.label.toUpperCase()}</div>
    <div style={{ fontSize: "13px", color: "#555", marginBottom: "10px", lineHeight: 1.5 }}>{s.desc}</div>
    {active && (
      <>
        <div style={{ fontFamily: "monospace", fontSize: "12px", background: "#fff", border: `1px solid ${s.color}40`, borderRadius: "6px", padding: "6px 10px", color: s.color, marginBottom: "8px" }}>{s.detail}</div>
        <div style={{ fontSize: "11px", fontWeight: 600, color: s.color, background: s.bg, borderRadius: "20px", padding: "3px 10px", display: "inline-block" }}>{s.tag}</div>
      </>
    )}
  </div>
);

export default function LoadBalancer() {
  const [activeLayer, setActiveLayer] = useState("l4");
  const [activeStatic, setActiveStatic] = useState("rr");
  const [activeDynamic, setActiveDynamic] = useState("lc");
  const [deployTab, setDeployTab] = useState(0);

  const selectedLayer = layers.find(l => l.id === activeLayer);
  const selectedDeploy = deployments[deployTab];

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", maxWidth: "900px", margin: "0 auto", padding: "32px 24px", background: "#fff", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "36px" }}>
        <h1 style={{ fontSize: "clamp(24px, 4vw, 38px)", fontWeight: 800, letterSpacing: "-0.02em", margin: 0, lineHeight: 1.15 }}>
          <span style={{ color: "#2C2C2A" }}>Load Balancer – </span>
          <span style={{ color: "#185FA5" }}>Types</span>
          <span style={{ color: "#2C2C2A" }}> & </span>
          <span style={{ color: "#D85A30" }}>Strategies</span>
        </h1>
        <p style={{ color: "#666", fontSize: "15px", marginTop: "12px", maxWidth: "600px", margin: "12px auto 0", lineHeight: 1.6 }}>
          Distributes incoming traffic across multiple servers to ensure no single server gets overloaded, improving performance, reliability and availability.
        </p>
      </div>

      {/* Section 1 – By Network Layer */}
      <Section>
        <SectionTitle color="#534AB7">1. Types by Network Layer (OSI Model)</SectionTitle>
        <div style={{ display: "flex", gap: "10px", marginBottom: "16px", flexWrap: "wrap" }}>
          {layers.map(l => (
            <button key={l.id} onClick={() => setActiveLayer(l.id)} style={{
              padding: "8px 20px", borderRadius: "8px", border: `2px solid ${activeLayer === l.id ? l.color : "#ddd"}`,
              background: activeLayer === l.id ? l.bg : "#fafafa", color: activeLayer === l.id ? l.color : "#666",
              fontWeight: 700, fontSize: "13px", cursor: "pointer", transition: "all 0.15s"
            }}>{l.label}</button>
          ))}
        </div>

        <div style={{ background: selectedLayer.bg, borderRadius: "14px", border: `2px solid ${selectedLayer.color}30`, padding: "20px 24px" }}>
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ flex: "0 0 auto", background: selectedLayer.color, color: "#fff", width: "64px", height: "64px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", fontWeight: 800 }}>
              {selectedLayer.icon}
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <div style={{ fontWeight: 700, fontSize: "16px", color: selectedLayer.color, marginBottom: "4px" }}>{selectedLayer.sub}</div>
              <div style={{ fontSize: "13px", color: "#555", marginBottom: "12px", fontStyle: "italic" }}>{selectedLayer.routes}</div>
              <ul style={{ padding: 0, margin: "0 0 12px" }}>
                {selectedLayer.bullets.map((b, i) => <CheckItem key={i} color={selectedLayer.color}>• {b}</CheckItem>)}
              </ul>
              <div style={{ fontSize: "12px", color: "#777" }}>
                <span style={{ fontWeight: 700, color: selectedLayer.color }}>Examples: </span>{selectedLayer.examples}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Section 2 – By Deployment Model */}
      <Section>
        <SectionTitle color="#0F6E56">2. Types by Deployment Model</SectionTitle>
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
          {deployments.map((d, i) => (
            <button key={i} onClick={() => setDeployTab(i)} style={{
              padding: "7px 16px", borderRadius: "8px", border: `2px solid ${deployTab === i ? d.color : "#ddd"}`,
              background: deployTab === i ? d.bg : "#fafafa", color: deployTab === i ? d.color : "#666",
              fontWeight: 700, fontSize: "12px", cursor: "pointer", transition: "all 0.15s"
            }}>{d.label}</button>
          ))}
        </div>

        <div style={{ background: selectedDeploy.bg, borderRadius: "14px", border: `2px solid ${selectedDeploy.color}30`, padding: "20px 24px" }}>
          <div style={{ fontSize: "28px", marginBottom: "8px" }}>{selectedDeploy.icon}</div>
          <div style={{ fontWeight: 700, fontSize: "15px", color: selectedDeploy.color, marginBottom: "6px" }}>{selectedDeploy.label}</div>
          <div style={{ fontSize: "13px", color: "#555", marginBottom: "12px" }}>{selectedDeploy.desc}</div>
          <ul style={{ padding: 0, margin: "0 0 12px" }}>
            {selectedDeploy.checks.map((c, i) => <CheckItem key={i} color={selectedDeploy.color}>{c}</CheckItem>)}
          </ul>
          <div style={{ fontSize: "12px", color: "#777" }}>
            <span style={{ fontWeight: 700, color: selectedDeploy.color }}>Examples: </span>{selectedDeploy.examples}
          </div>
        </div>
      </Section>

      {/* Summary callout */}
      <div style={{ background: "#FAEEDA", border: "1.5px solid #EF9F27", borderRadius: "10px", padding: "12px 18px", marginBottom: "32px", display: "flex", alignItems: "center", gap: "10px" }}>
        <span style={{ fontSize: "20px" }}>💡</span>
        <span style={{ fontSize: "14px", color: "#633806", fontWeight: 500 }}>
          <strong>Summary:</strong> Choose based on your use case, performance needs, and infrastructure requirements.
        </span>
      </div>

      {/* Static Load Balancing */}
      <Section>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <SectionTitle color="#534AB7">A. Static Load Balancing</SectionTitle>
          <Badge color="#534AB7" bg="#EEEDFE">Uses predefined rules</Badge>
        </div>
        <p style={{ fontSize: "13px", color: "#666", margin: "0 0 14px" }}>Best when server capacities are similar and workloads are predictable.</p>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {staticStrategies.map(s => (
            <StrategyCard key={s.id} s={s} active={activeStatic === s.id} onClick={setActiveStatic} />
          ))}
        </div>
      </Section>

      {/* Dynamic Load Balancing */}
      <Section>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <SectionTitle color="#D85A30">B. Dynamic Load Balancing</SectionTitle>
          <Badge color="#993C1D" bg="#FAECE7">Considers real-time server conditions</Badge>
        </div>
        <p style={{ fontSize: "13px", color: "#666", margin: "0 0 14px" }}>Best when workloads are unpredictable and server performance varies.</p>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {dynamicStrategies.map(s => (
            <StrategyCard key={s.id} s={s} active={activeDynamic === s.id} onClick={setActiveDynamic} />
          ))}
        </div>
      </Section>

      {/* Static vs Dynamic note */}
      <div style={{ background: "#FFF9E6", border: "1.5px dashed #EF9F27", borderRadius: "10px", padding: "14px 20px", marginBottom: "32px" }}>
        <div style={{ fontWeight: 700, fontSize: "13px", color: "#633806", marginBottom: "6px" }}>📌 Note</div>
        <div style={{ fontSize: "13px", color: "#555", lineHeight: 1.7 }}>
          <strong>Static</strong> = Simpler, good for predictable workloads.<br />
          <strong>Dynamic</strong> = Smarter, best for varying loads.
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", borderTop: "2px solid #eee", paddingTop: "20px" }}>
        <div style={{ flex: 1, minWidth: "220px", background: "#FAECE7", borderRadius: "10px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "20px" }}>🎯</span>
          <span style={{ fontSize: "13px", color: "#993C1D" }}>
            <strong>Goal:</strong> Distribute traffic efficiently, optimize resource usage, prevent server overload and ensure high availability.
          </span>
        </div>
        <div style={{ flex: 1, minWidth: "220px", background: "#FAEEDA", borderRadius: "10px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "20px" }}>⭐</span>
          <span style={{ fontSize: "13px", color: "#633806" }}>
            <strong>Result:</strong> Better Performance · Reliability · Scalability · User Satisfaction
          </span>
        </div>
      </div>
    </div>
  );
}
